import React, { useEffect, useRef, useState } from "react";
import Select, { type Theme } from "react-select";
import { useNavigate, useOutletContext } from "react-router";
import { toast } from "react-toastify";
import {
  ICmsQuestionType,
  ICmsSubject,
  createTeacherQuestion,
  getCmsQuestionTypes,
  getCmsTeacherSubjects,
} from "../../../../api/crmContent.service";
import { getAllDomainsBySubject } from "../../../../api/domain.service";
import { getStandardOptionsByDomain } from "../../../../api/standard.service";
import { getStateAll } from "../../../../api/subject.service";
import { ISelectElement } from "../../../../model/interface/keyValuePair";

import { dokOptions, IDokOption } from "../../../../utils/dokOptions";
import { IUserContentOutletContext } from "./UserContentLayout";
import { numberOfPartsOptions, parsePassageIds } from "./questionHelpers";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import store from "../../../../redux/store";

const reactSelectTheme = (theme: Theme): Theme => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary25: "#B3B3B3",
    primary: "#041E42",
  },
});

export const QuestionNew: React.FC = () => {
  const { userId, userInfo } = useOutletContext<IUserContentOutletContext>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(false);
  const [subjects, setSubjects] = useState<Array<ISelectElement>>([]);
  const [domains, setDomains] = useState<Array<ISelectElement>>([]);
  const [standards, setStandards] = useState<Array<ISelectElement>>([]);
  const [questionTypes, setQuestionTypes] = useState<Array<ISelectElement>>([]);
  const [states, setStates] = useState<Array<ISelectElement>>([]);

  const [selectedSubject, setSelectedSubject] = useState<ISelectElement | null>(
    null
  );
  const [selectedDomain, setSelectedDomain] = useState<ISelectElement | null>(
    null
  );
  const [selectedStandard, setSelectedStandard] =
    useState<ISelectElement | null>(null);
  const [selectedDok, setSelectedDok] = useState<IDokOption | null>(null);
  const [selectedQuestionType, setSelectedQuestionType] =
    useState<ISelectElement | null>(null);
  const [selectedState, setSelectedState] = useState<ISelectElement | null>(
    null
  );
  const [cmsAuthorNotes, setCmsAuthorNotes] = useState<string>("");

  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [numberOfParts, setNumberOfParts] = useState<ISelectElement | null>(
    null
  );
  const [passageIdsInput, setPassageIdsInput] = useState<string>("");
  const [isCreating, setIsCreating] = useState<boolean>(false);

  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getCmsTeacherSubjects(userId).then((response) => {
        const options: Array<ISelectElement> = (response.data ?? []).map(
          (subject: ICmsSubject) => ({
            value: String(subject.subjectId),
            label: subject.name,
          })
        );
        setSubjects(options);
      }),
      getCmsQuestionTypes().then((response) => {
        const options: Array<ISelectElement> = (response.data ?? []).map(
          (qt: ICmsQuestionType) => ({
            value: String(qt.questionTypeId),
            label: qt.displayName ?? qt.name,
          })
        );
        setQuestionTypes(options);
      }),
      getStateAll(9999, 1).then((response) => {
        const options: Array<ISelectElement> = (
          (response as any)?.data?.data ?? []
        ).map((s: { stateId: number; name: string }) => ({
          value: String(s.stateId),
          label: s.name,
        }));
        setStates(options);
      }),
    ]).finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => {
    if (showDialog && dialogRef.current) {
      dialogRef.current.showModal();
    }
  }, [showDialog]);

  const handleSubjectChange = (option: ISelectElement | null) => {
    setSelectedSubject(option);
    setSelectedDomain(null);
    setSelectedStandard(null);
    setDomains([]);
    setStandards([]);
    if (option) {
      getAllDomainsBySubject(Number(option.value)).then((response) => {
        const opts: Array<ISelectElement> = (response.data?.data ?? []).map(
          (d: { domainId: number; name: string }) => ({
            value: String(d.domainId),
            label: d.name,
          })
        );
        setDomains(opts);
      });
    }
  };

  const handleDomainChange = (option: ISelectElement | null) => {
    setSelectedDomain(option);
    setSelectedStandard(null);
    setStandards([]);
    if (option) {
      getStandardOptionsByDomain(Number(option.value)).then(setStandards);
    }
  };

  const isCreateEnabled =
    selectedQuestionType !== null &&
    selectedState !== null &&
    selectedSubject !== null &&
    selectedDomain !== null &&
    selectedStandard !== null &&
    selectedDok !== null;

  const handleCreate = () => {
    setShowDialog(true);
  };

  const handleCloseModal = () => {
    dialogRef.current?.close();
    setShowDialog(false);
  };

  const handleContinue = async () => {
    setIsCreating(true);
    const passageIds = parsePassageIds(passageIdsInput);

    try {
      const response = await createTeacherQuestion({
        userId: Number(userId),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        cmsAuthorUserId: (store.getState() as any).profile?.userId || undefined,
        cmsAuthorNotes: cmsAuthorNotes || undefined,
        districtId: userInfo!.districtId,
        stateId: Number(selectedState!.value),
        isPublished: false,
        questionTypeId: Number(selectedQuestionType!.value),
        standardId: Number(selectedStandard!.value),
        dok: selectedDok!.value,
        passageIds,
      });
      handleCloseModal();
      navigate(`../${response.data}`, {
        relative: "path",
        state: {
          numberOfParts: Number(numberOfParts!.value),
          passageIds,
        },
      });
    } catch (error: any) {
      const message =
        error?.response?.data ?? "Failed to create question. Please try again.";
      toast.error(message);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <>
      <div className="flex-1 overflow-auto">
        <div className="h-auto min-h-half w-full px-8 py-6">
          <div className="relative py-1">
            <div className="pt-6 border-b pb-6">
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-3">
                  <label className="text-sm text-gray-700">Question Type</label>
                  <Select
                    className="w-full mt-1 text-sm"
                    options={questionTypes}
                    onChange={(option) =>
                      setSelectedQuestionType(option as ISelectElement | null)
                    }
                    value={selectedQuestionType}
                    isClearable={true}
                    isSearchable={true}
                    theme={reactSelectTheme}
                    isLoading={loading}
                  />
                </div>

                <div className="col-span-3">
                  <label className="text-sm text-gray-700">State</label>
                  <Select
                    className="w-full mt-1 text-sm"
                    options={states}
                    onChange={(option) =>
                      setSelectedState(option as ISelectElement | null)
                    }
                    value={selectedState}
                    isClearable={true}
                    isSearchable={true}
                    theme={reactSelectTheme}
                    isLoading={loading}
                  />
                </div>

                <div className="col-span-3">
                  <label className="text-sm text-gray-700">Subject</label>
                  <Select
                    className="w-full mt-1 text-sm"
                    options={subjects}
                    onChange={(option) =>
                      handleSubjectChange(option as ISelectElement | null)
                    }
                    value={selectedSubject}
                    isClearable={true}
                    isSearchable={true}
                    theme={reactSelectTheme}
                    isLoading={loading}
                  />
                </div>

                <div className="col-span-3">
                  <label className="text-sm text-gray-700">Domain</label>
                  <Select
                    className="w-full mt-1 text-sm"
                    options={domains}
                    onChange={(option) =>
                      handleDomainChange(option as ISelectElement | null)
                    }
                    value={selectedDomain}
                    isClearable={true}
                    isSearchable={true}
                    isDisabled={!selectedSubject}
                    theme={reactSelectTheme}
                  />
                </div>
              </div>

              <div className="grid grid-cols-12 gap-6 mt-5">
                <div className="col-span-3">
                  <label className="text-sm text-gray-700">Standard</label>
                  <Select
                    className="w-full mt-1 text-sm"
                    options={standards}
                    onChange={(option) =>
                      setSelectedStandard(option as ISelectElement | null)
                    }
                    value={selectedStandard}
                    isClearable={true}
                    isSearchable={true}
                    isDisabled={!selectedDomain}
                    theme={reactSelectTheme}
                  />
                </div>

                <div className="col-span-3">
                  <label className="text-sm text-gray-700">DOK</label>
                  <Select
                    className="w-full mt-1 text-sm"
                    options={dokOptions}
                    onChange={(option) =>
                      setSelectedDok(option as IDokOption | null)
                    }
                    value={selectedDok}
                    isClearable={true}
                    isSearchable={true}
                    theme={reactSelectTheme}
                  />
                </div>

                <div className="col-span-6 flex flex-col">
                  <label className="text-sm text-gray-700">
                    CMS Author Notes{" "}
                    <span className="text-gray-400 font-light">(optional)</span>
                  </label>
                  <textarea
                    rows={4}
                    className="flex-1 w-full mt-1 text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-color resize-none"
                    value={cmsAuthorNotes}
                    onChange={(e) => setCmsAuthorNotes(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 mt-5">
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={!isCreateEnabled}
                  className="inline-flex items-center text-sm text-white dark:text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create
                </button>
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="inline-flex items-center text-sm rounded-lg text-gray-700 border border-gray-300 gap-2 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color py-1.5 px-4"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <dialog
        ref={dialogRef}
        className="rounded-lg p-0 shadow-xl backdrop:bg-black backdrop:bg-opacity-50"
        onClose={() => setShowDialog(false)}
      >
        <div className="p-6 min-w-96">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Create a new question
          </h2>
          <p className="text-sm text-gray-600 mb-6">
            Choose the number of parts this question will have and enter any
            Passage Ids that will be associated to the question.
          </p>

          <div className="mb-4">
            <label className="text-sm text-gray-700">Number of Parts</label>
            <Select
              className="w-full mt-1 text-sm"
              options={numberOfPartsOptions}
              onChange={(option) =>
                setNumberOfParts(option as ISelectElement | null)
              }
              value={numberOfParts}
              theme={reactSelectTheme}
            />
          </div>

          <div className="mb-6">
            <label className="text-sm text-gray-700">
              Passage IDs{" "}
              <span className="text-gray-400 font-light">
                (optional, comma-separated)
              </span>
            </label>
            <input
              type="text"
              className="w-full mt-1 text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-color"
              value={passageIdsInput}
              onChange={(e) => setPassageIdsInput(e.target.value)}
              placeholder="e.g. 79, 80"
            />
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={handleContinue}
              disabled={isCreating || numberOfParts === null}
              className="inline-flex items-center text-sm text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreating ? "Creating..." : "Continue"}
            </button>
            <button
              type="button"
              onClick={handleCloseModal}
              className="inline-flex items-center text-sm rounded-lg text-gray-700 border border-gray-300 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color py-1.5 px-4"
            >
              Cancel
            </button>
          </div>
        </div>
      </dialog>
    </>
  );
};
